import { useEffect } from 'react'
import { DateTime } from 'luxon'

import { adminFetchSingleUserIfNeeded } from '@/actions/admin/adminAllUsersActions'
import {
  adminIsUserInFreeTrial,
  adminIsUserNoServiceFreeTrial,
  selectPrimaryMembershipForUser,
} from '@/selectors/user.selectors'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Alert } from '@/ui'

/** Sits directly above dense bookkeeping content, so it stays tight. */
const COMPACT = 'my-1 p-3'

/**
 * Tells whoever is working this account whether it is on a free trial, and whether that
 * trial includes bookkeeping service — the question that decides whether to do the books.
 *
 * Fetches the user itself: several of the bookkeeping views this renders on do not load
 * the admin user record, and without it the alert would silently never appear.
 */
export const UserFreeTrialAlert = ({
  userId,
}: {
  userId: number | string | undefined
}) => {
  const dispatch = useAppDispatch()
  const isInFreeTrial = useReselector(adminIsUserInFreeTrial, userId)
  const isUnserviced = useReselector(adminIsUserNoServiceFreeTrial, userId)
  const membership = useReselector(selectPrimaryMembershipForUser, userId)

  useEffect(() => {
    if (userId) {
      dispatch(adminFetchSingleUserIfNeeded(Number(userId)))
    }
  }, [dispatch, userId])

  if (!isInFreeTrial) {
    return null
  }

  const trialEnds = membership?.trialEndDate
    ? DateTime.fromISO(membership.trialEndDate).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_SHORT
      )
    : 'unknown'
  const promoCode = membership?.freeTrialPromoCode ?? 'none'

  if (isUnserviced) {
    return (
      <Alert variant="warning" className={COMPACT}>
        <b>Free trial with no bookkeeping service</b>
        Trial code: {promoCode} · Ends: {trialEnds} · do not catch up or close
        their books
      </Alert>
    )
  }

  return (
    <Alert variant="default" className={COMPACT}>
      <b>Free trial — service normally</b>
      Trial code: {promoCode} · Ends: {trialEnds} · includes bookkeeping,
      catch-up included
    </Alert>
  )
}
